import axios from "axios";
import { toPaydDetail, toPaydJobs } from "./paydData";

/**
 * PAYD talks to the PORTAL, not to an API host.
 *
 * That makes it the odd one out and dictates everything here:
 *
 *   - It authenticates with a Flask session COOKIE, not the Bearer token the
 *     rest of the app carries — so these calls go through their own axios
 *     instance with `withCredentials`, not through apiClient, whose
 *     interceptor would attach the wrong credential to the wrong host.
 *   - The cookie has no SameSite attribute, so it defaults to Lax and is not
 *     sent cross-site. Dev therefore goes through a same-origin proxy, and
 *     production only works where the app is served from the portal's own
 *     domain. Same constraint as Hegic's digital passport.
 *   - The list is a POST with a form body, and pages by page NUMBER.
 *
 * The images are on the same host under /media and need no credential at all.
 */

export const PAYD_PORTAL =
  process.env.NODE_ENV === "development"
    ? "/payd-portal"
    : "https://payd.portal.camcom.ai";

const portal = axios.create({
  baseURL: PAYD_PORTAL,
  /**
   * Credentials in development only, and that is not a hedge.
   *
   * In development the request is same-origin (the /payd-portal proxy above),
   * so the flag costs nothing and keeps the door open for the
   * PAYD_SESSION_COOKIE escape hatch in setupProxy.js.
   *
   * In production the request is cross-origin, and a credentialed cross-origin
   * request requires Access-Control-Allow-Credentials: true on BOTH the
   * preflight and the response. This portal does not send it. Probed:
   *
   *   OPTIONS https://payd.portal.camcom.ai/dashboard
   *     Access-Control-Allow-Origin: <the Origin, reflected verbatim>
   *     Access-Control-Allow-Methods: DELETE, GET, HEAD, OPTIONS, PATCH, POST, PUT
   *     (no Access-Control-Allow-Credentials)
   *
   * So the browser discarded every response and axios surfaced it as a bare
   * "Network Error" with no status to explain it. Nothing is lost by dropping
   * the flag: the portal authenticates nobody today — the same finding that
   * made the dev proxy's cookie header an unused escape hatch — and it
   * reflects any Origin, so the plain request succeeds from any deployment.
   *
   * If that host ever does start requiring a session, the fix is a same-origin
   * proxy in nginx mirroring the dev one, not this flag.
   */
  withCredentials: process.env.NODE_ENV === "development",
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

/**
 * One page of jobs. Pages are 1-based.
 *
 * `status` is the review-state filter the dashboard's own dropdown sends —
 * 0 All, 1 Completed, 2 Under Review, 3 Review Completed (confirmed against a
 * real captured request for each value, including the unscoped "0" alongside
 * a date range). `ref_num` always rides along, empty, because the real
 * portal's own request always carries it even with nothing typed into it.
 *
 * `from_datetime`/`to_datetime` ARE real server-side parameters — confirmed
 * against a captured request — contrary to what every status-only curl
 * seen before that one implied. They scope the NumberPlate/Odo/Vin read
 * counters too, not just which rows come back, so the date range has to be a
 * real request parameter rather than a client-side slice of one page: a
 * client-side filter would leave those three counters reporting the
 * account's lifetime totals while the table beneath them claimed to be
 * showing a date-scoped page. Sent as "YYYY-MM-DD HH:MM:SS", start of day to
 * end of day, matching the captured request exactly.
 */
export const getPaydJobs = async (page = 1, { status = "0", startDate, endDate } = {}) => {
  const body = { page: String(page), ref_num: "", status: String(status) };
  if (startDate) body.from_datetime = `${startDate} 00:00:00`;
  if (endDate) body.to_datetime = `${endDate} 23:59:59`;

  const response = await portal.post(
    "/dashboard",
    new URLSearchParams(body).toString(),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
    },
  );
  // The response reports a row offset, not a page; the page is what we asked
  // for.
  return { ...toPaydJobs(response.data), page };
};

/** One job's three photographs and readings. */
export const getPaydJob = async (refNum) => {
  if (!refNum) throw new Error("getPaydJob: ref_num is required");
  const response = await portal.get("/vehicle_job_details", {
    params: { ref_num: refNum },
  });
  return toPaydDetail(response.data, refNum);
};

/** An image path from the detail record, as a URL the browser can load. */
export const paydImageUrl = (path) => {
  if (typeof path !== "string" || !path.trim()) return "";
  const clean = path.trim();
  if (/^https?:\/\//i.test(clean)) return clean;
  return `${PAYD_PORTAL}/${clean.replace(/^\/+/, "")}`;
};
